<template>
  <div>
    <!-- Filtros -->
    <v-card class="mb-5 pa-4" outlined>
      <v-row align="center">
        <v-col cols="6" sm="3">
          <v-select
            v-model="selectedYear"
            :items="years"
            label="Año"
            dense
            outlined
            hide-details
          />
        </v-col>
        <v-col cols="6" sm="3">
          <v-select
            v-model="selectedMonth"
            :items="months"
            item-text="text"
            item-value="value"
            label="Mes"
            dense
            outlined
            hide-details
          />
        </v-col>
      </v-row>
    </v-card>

    <!-- Gráfico de líneas -->
    <v-card outlined class="mb-5" min-height="420">
      <v-card-title class="subtitle-1 font-weight-bold d-flex align-center flex-wrap">
        <v-icon left small>mdi-chart-line</v-icon>
        MRR día a día
        <span class="body-2 font-weight-regular grey--text ml-2">
          {{ months.find(m => m.value === selectedMonth).text }} {{ selectedYear }}
        </span>
        <span class="body-2 font-weight-regular grey--text ml-auto">
          <v-icon x-small color="orange">mdi-circle</v-icon> Forecast &nbsp;
          <v-icon x-small color="primary">mdi-circle</v-icon> Real
        </span>
      </v-card-title>
      <p v-if="points.length && canSeeContracts" class="caption grey--text px-4 mb-2">
        Clic en un punto del gráfico para ver los contratos con variación ese día.
      </p>
      <v-card-text class="pb-6">
        <div v-if="loadingSeries" class="d-flex justify-center align-center py-8">
          <v-progress-circular indeterminate color="primary" />
        </div>
        <div v-else-if="points.length === 0" class="text-center grey--text py-8">
          Sin snapshots de MRR para este mes
        </div>
        <div v-else style="position: relative; height: 280px; width: 100%;">
          <line-chart :chart-data="chartData" :options="chartOptions" />
        </div>
      </v-card-text>
    </v-card>

    <!-- Variación por contrato (drill-down) -->
    <v-card v-if="selectedDate" outlined class="mb-5">
      <v-card-title class="subtitle-1 font-weight-bold d-flex align-center">
        <v-icon left small>mdi-swap-vertical</v-icon>
        Variación por contrato — {{ selectedDate }}
        <span v-if="previousDate" class="body-2 font-weight-regular grey--text ml-2">
          vs. {{ previousDate }}
        </span>
      </v-card-title>
      <v-card-text>
        <div v-if="loadingChanges" class="d-flex justify-center align-center py-6">
          <v-progress-circular indeterminate color="primary" />
        </div>
        <div v-else-if="!previousDate" class="text-center grey--text py-6">
          No hay un snapshot anterior disponible para comparar.
        </div>
        <div v-else-if="changes.length === 0" class="text-center grey--text py-6">
          Ningún contrato varió ese día.
        </div>
        <v-simple-table v-else dense>
          <thead>
            <tr>
              <th>Contrato</th>
              <th>Empresa</th>
              <th>País</th>
              <th>Línea de negocio</th>
              <th>Canal</th>
              <th class="text-right">MRR anterior</th>
              <th class="text-right">MRR actual</th>
              <th class="text-right">Variación</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in changes" :key="row.contrato">
              <td>{{ row.contrato }}</td>
              <td>{{ row.empresa || '—' }}</td>
              <td>{{ row.pais || '—' }}</td>
              <td>{{ row.lineaNegocio || '—' }}</td>
              <td>{{ row.canal || '—' }}</td>
              <td class="text-right">US${{ formatNumber(row.previousUsd) }}</td>
              <td class="text-right">US${{ formatNumber(row.currentUsd) }}</td>
              <td class="text-right">
                <delta-badge :usd="row.deltaUsd" :pct="row.deltaPct !== null ? row.deltaPct / 100 : null" />
              </td>
            </tr>
          </tbody>
        </v-simple-table>
      </v-card-text>
    </v-card>

    <v-snackbar v-model="showSnackbar" :color="snackbarColor" timeout="3000" top>
      {{ snackbarMessage }}
    </v-snackbar>
  </div>
</template>

<script>
import { Line } from 'vue-chartjs';
import MrrDailyService from '../services/mrr-daily.service';
import DeltaBadge from './DeltaBadge.vue';
import { utilsMixin } from '../mixins/utilsMixin';
import { can } from '../utils/permissions';

const LineChart = {
  extends: Line,
  props: ['chartData', 'options'],
  mounted() {
    this.renderChart(this.chartData, this.options);
  },
  watch: {
    chartData: {
      deep: true,
      handler(newData) {
        if (this.$data._chart) {
          this.$data._chart.destroy();
        }
        this.renderChart(newData, this.options);
      },
    },
  },
};

export default {
  name: 'MrrDailyPanel',
  components: { LineChart, DeltaBadge },
  mixins: [utilsMixin],
  data() {
    const today = new Date();
    const currentYear = today.getFullYear();
    return {
      selectedYear: currentYear,
      selectedMonth: today.getMonth() + 1,
      years: [currentYear - 1, currentYear, currentYear + 1],
      months: [
        { value: 1, text: 'Enero' },
        { value: 2, text: 'Febrero' },
        { value: 3, text: 'Marzo' },
        { value: 4, text: 'Abril' },
        { value: 5, text: 'Mayo' },
        { value: 6, text: 'Junio' },
        { value: 7, text: 'Julio' },
        { value: 8, text: 'Agosto' },
        { value: 9, text: 'Septiembre' },
        { value: 10, text: 'Octubre' },
        { value: 11, text: 'Noviembre' },
        { value: 12, text: 'Diciembre' },
      ],
      loadingSeries: false,
      points: [],
      selectedDate: null,
      previousDate: null,
      loadingChanges: false,
      changes: [],
      // La variación por contrato requiere `mrr-resume:view-contracts`.
      canSeeContracts: can('mrr-resume:view-contracts'),
      showSnackbar: false,
      snackbarMessage: '',
      snackbarColor: 'red',
    };
  },
  computed: {
    chartData() {
      const labels = this.points.map((p) => Number(p.date.slice(8, 10)));
      return {
        labels,
        datasets: [
          {
            label: 'MRR (USD)',
            data: this.points.map((p) => p.mrrUSD),
            borderColor: '#1976D2',
            backgroundColor: 'rgba(25, 118, 210, 0.08)',
            pointBackgroundColor: this.points.map((p) => (p.estimado ? '#FB8C00' : '#1976D2')),
            pointRadius: 4,
            pointHoverRadius: 6,
            borderWidth: 2,
            fill: true,
            tension: 0.25,
          },
        ],
      };
    },
    // Rango del eje Y con margen: con pocos puntos (o uno solo), el autoescalado
    // de Chart.js queda casi plano y aun así dibuja ~11 líneas de grilla con
    // etiquetas casi idénticas, desbordando el alto del gráfico. Se fija un
    // rango con colchón proporcional al valor para que se vea prolijo desde
    // el primer día del mes.
    yAxisRange() {
      const values = this.points.map((p) => p.mrrUSD);
      const min = Math.min(...values);
      const max = Math.max(...values);
      const padding = Math.max((max - min) * 0.15, max * 0.02, 50);
      return {
        min: Math.max(0, Math.floor(min - padding)),
        max: Math.ceil(max + padding),
      };
    },
    chartOptions() {
      return {
        responsive: true,
        maintainAspectRatio: false,
        legend: { display: false },
        scales: {
          xAxes: [{ gridLines: { display: false }, scaleLabel: { display: true, labelString: 'Día' } }],
          yAxes: [
            {
              ticks: {
                ...this.yAxisRange,
                maxTicksLimit: 6,
                callback: (v) => `US$${this.formatNumber(v)}`,
              },
              gridLines: { color: 'rgba(0,0,0,0.05)' },
            },
          ],
        },
        tooltips: {
          callbacks: {
            label: (item) => {
              const p = this.points[item.index];
              const base = `US$${this.formatNumber(p.mrrUSD)}`;
              return p.estimado ? `${base} (forecast)` : base;
            },
          },
        },
        onClick: (evt, elements) => {
          if (elements && elements.length) {
            const idx = elements[0]._index;
            this.onPointClick(idx);
          }
        },
      };
    },
  },
  watch: {
    selectedYear() {
      this.loadSeries();
    },
    selectedMonth() {
      this.loadSeries();
    },
  },
  created() {
    this.loadSeries();
  },
  methods: {
    formatNumber(n) {
      return new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(Number(n) || 0);
    },
    async loadSeries() {
      this.loadingSeries = true;
      this.selectedDate = null;
      this.changes = [];
      const res = await MrrDailyService.getSeries(this.selectedYear, this.selectedMonth);
      if (res.status === 'ok') {
        this.points = res.data;
      } else {
        this.points = [];
        this.showErrorSnackbar(res.message);
      }
      this.loadingSeries = false;
    },
    async onPointClick(index) {
      const point = this.points[index];
      if (!point || !this.canSeeContracts) return;
      this.selectedDate = point.date;
      this.loadingChanges = true;
      this.changes = [];
      this.previousDate = null;
      const res = await MrrDailyService.getContractChanges(
        point.date,
        this.selectedYear,
        this.selectedMonth
      );
      if (res.status === 'ok') {
        this.changes = res.data;
        this.previousDate = res.meta.previousDate;
      } else {
        this.showErrorSnackbar(res.message);
      }
      this.loadingChanges = false;
    },
  },
};
</script>
